/**
 * The one definition of what a channel post looks like — caption layout, the truncation
 * budget, and the button row.
 *
 * ## Why this exists
 *
 * Two definitions of a channel post existed before this file: the Worker's `renderChannelPost`
 * (`apps/api/src/services/contentOps.ts`, used by "Post now" and the cron drain) and
 * `infra/lib/channel-post.mjs` (used by the offload script and the button-repair script). They
 * disagreed on six fields — engagement, credit, the code-block label, even the button text —
 * because there was never a moment either author could see the other's version. `renderChannelPost`
 * is now a thin wrapper around this module; `infra/lib/channel-post.mjs` is deleted.
 *
 * ## Why this file has no imports
 *
 * It has to be importable two ways: by the Worker's bundler (Vite/esbuild, which resolves
 * extensionless relative imports) and directly by plain `node` running an `infra/*.mjs` script
 * (which, per Node's native TypeScript support, erases types but still requires an explicit file
 * extension on every relative specifier — `./foo` does not resolve, `./foo.ts` does, and this
 * project's `moduleResolution: "Bundler"` tsconfig does not allow writing the latter). The
 * precedent (`apps/api/src/services/titles.ts`, imported directly by `infra/fix-titles.mjs`)
 * works for exactly this reason: it has no relative imports to break under either resolver. This
 * file follows the same rule rather than reaching for a bundler-config change to route around it.
 *
 * That means `escapeHtml`, `personForCaption` and `platformLabel` below are each a few lines of
 * logic that already exist elsewhere in the codebase — `apps/api/src/lib/telegram.ts`'s
 * `escapeHtml`, `apps/api/src/services/authors.ts`'s `personFrom`, and the label half of
 * `apps/api/src/services/engagement.ts`'s `normalisePlatform`. They are not reimported here
 * because those files are not self-contained (they pull in the Drizzle schema), and they are not
 * reimplemented from scratch either: `test/channelPost.test.ts` in this package asserts this
 * file's copies agree with the Worker's originals across the real corpus, so a change to one
 * without the other fails a test rather than drifting silently.
 */

/** Telegram rejects a `sendPhoto`/`editMessageCaption` call outright above this. */
export const CAPTION_LIMIT = 1024;

function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** How many extra characters `escapeHtml` adds for one input character — 0 for anything plain. */
const ESCAPE_GROWTH: Record<string, number> = { '&': 4, '<': 3, '>': 3, '"': 5 };

/**
 * The prompt goes in a labelled code block, like every other surface that shows one.
 *
 * A bare `<pre>` lets Telegram's own language detector guess at the contents, and a prompt full
 * of `{argument}`/`[topic]` placeholder syntax reads enough like markup to be headed "XML" — this
 * exact defect (D57) shipped on the bot card, the full-prompt message, and this channel post
 * before being fixed each time; a bare `<pre>` here would be the fourth surface repeating it.
 */
function codeBlock(body: string): string {
  return `<pre><code class="language-Prompt">${escapeHtml(body)}</code></pre>`;
}

/** Exactly what `codeBlock` adds around its argument, measured rather than counted by hand. */
export const CODE_BLOCK_OVERHEAD = codeBlock('').length;

/**
 * The person to credit — never the raw `"<name> via <platform>"` string a source's attribution
 * column stores. Mirrors `apps/api/src/services/authors.ts`'s `personFrom`; see the file header
 * for why this is a second copy rather than a shared import, and `channelPost.test.ts` for the
 * assertion that the two agree.
 */
export function personForCaption(attribution: string | null | undefined): string | null {
  const raw = (attribution ?? '').trim();
  if (!raw) return null;

  const via = /^(.+?)\s+via\s+\S+$/.exec(raw);
  if (via) return via[1]!.trim();

  const repo = /^([A-Za-z0-9][A-Za-z0-9-]*)\/[^\s]+/.exec(raw);
  if (repo) return repo[1]!;

  const separated = raw.split(/\s+[·•|]\s+/)[0]?.trim();
  const candidate = separated || raw;

  // A bare hostname is a source, never a contributor — the whole point of this function existing
  // (D65-second). Missing this branch is exactly the discrepancy `channelCaptionParity.test.ts`
  // exists to catch: without it, a channel post for a `youmind.com`-only prompt would credit
  // "youmind.com" as if it were a person.
  if (/^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+\/?$/i.test(candidate)) return null;
  if (!/[A-Za-z]/.test(candidate)) return null;

  return candidate || null;
}

/**
 * A source name into its display label — `civitai.com` → `Civitai`, `meigen.ai` → `meigen`.
 *
 * A full mirror of `normalisePlatform` (`engagement.ts`), **including its literal brand-casing
 * table** — `youmind` and `meigen` are deliberately lowercase, `PromptDen` is deliberately
 * internally capitalised, and no algorithm derives that from the hostname; it has to be the same
 * table or the two will disagree on exactly the names a reader will actually see. A first attempt
 * here used `hostname[0].toUpperCase() + rest` instead of copying the table, which is wrong for
 * 4 of the 6 source names in the corpus of prompts a channel post can actually carry — caught by
 * `channelCaptionParity.test.ts` (apps/api), which checks this function against the real one for
 * every `source_name` in that corpus before this shipped, not after.
 */
const PLATFORM_LABELS: Record<string, string> = {
  'civitai.com': 'Civitai',
  'youmind.com': 'youmind',
  'promptden.com': 'PromptDen',
  'meigen.ai': 'meigen',
  'github.com': 'GitHub',
};

function platformLabel(sourceName: string): string {
  const name = sourceName.trim();
  const hasScheme = /^[a-z]+:\/\//i.test(name);
  if (!hasScheme && name.split('/').length === 2 && !name.includes(' ')) return 'GitHub';

  const host = name
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();

  // Not host-shaped — one of our own labels ("PromptVault Samples") or a bare channel name
  // ("ai_farshad"). Keep it verbatim rather than mangling it into something that looks like a
  // domain: `normalisePlatform` returns the original `name` here, not the lowercased `host`.
  if (!host.includes('.')) return name;

  return PLATFORM_LABELS[host] ?? host;
}

export interface ChannelEngagement {
  /** The stored headline, verbatim — never a recomputed sum (D53). */
  value: number;
  /** Shown under its own name even when unrecognised (D51) — never coerced to "likes". */
  metric: string;
  /** Raw `source_name` column; turned into a label by `platformLabel`. */
  sourceName: string;
}

export interface ChannelCaptionInput {
  title: string;
  body: string;
  /** Raw `author_attribution`; reduced to a person by `personForCaption`. Omit for no credit line. */
  authorAttribution?: string | null;
  /** Omit, or pass `null`, for a prompt with no source engagement — the line disappears rather than showing a zero. */
  engagement?: ChannelEngagement | null;
}

export interface ChannelCaption {
  text: string;
  /** Whether the body had to be cut to fit `CAPTION_LIMIT`. Decides the 📄 Full prompt button. */
  truncated: boolean;
}

/**
 * Generator syntax, removed before the excerpt is cut — Phase 9 §3. Mirrors
 * `apps/api/src/services/titles.ts`'s `stripPromptSyntax` (same regexes, same "only weights
 * falls back to the original text rather than empty" rule); see this file's header for why that
 * is a second copy rather than a shared import, and `channelPost.test.ts` for the assertion that
 * the two agree.
 *
 * Deliberately narrower than `cleanTitle`'s own list: no generic "any `<...>` token" catch-all.
 * A title with nothing left has other fallbacks to reach for; an excerpt does not, and a body
 * legitimately discussing HTML or scripting (a code prompt using `<script>` as its own example)
 * must have that content survive to be escaped for display, not silently removed. The pre-existing
 * "hostile body" test below is what catches a regression here — it expects `<script>` to come out
 * HTML-escaped, not gone.
 */
const GENERATOR_SYNTAX = [
  /<(?:lora|lyco|lycoris|hypernet|embedding|ti)\s*:[^>]*>/gi,
  /<(?:lora|lyco|lycoris|hypernet|embedding|ti)\s*:[^>]*$/gi,
  /\(?\s*\bembedding:[\w./\\-]+(?::[\d.]+)?\s*\)?/gi,
];

function stripWeights(value: string): string {
  let out = value;
  for (let i = 0; i < 4; i++) {
    const next = out.replace(/\(([^()]*?):\s*[\d.]+\s*\)/g, '$1');
    if (next === out) break;
    out = next;
  }
  return out.replace(/[[\]{}]/g, '').replace(/\(\s*\)/g, '');
}

export function stripPromptSyntax(text: string): string {
  const original = (text ?? '').trim();
  let working = original;
  for (const re of GENERATOR_SYNTAX) working = working.replace(re, ' ');
  working = stripWeights(working).replace(/\s+/g, ' ').trim();
  return working || original;
}

/**
 * Fit `body` into `budget` **rendered** characters, cutting on a word boundary where one is
 * available. The budget arithmetic on its own, so the edges are testable without going through
 * full caption rendering: exactly at the limit, one character over, a body that ends mid-word,
 * and a body whose `&`/`<`/`>` characters would otherwise grow past the limit once escaped.
 *
 * `budget` is measured in **post-escaping** characters, because that is what Telegram counts
 * against the 1,024 cap. Cutting the raw string to `budget` raw characters and escaping
 * afterward — what both predecessors of this function did — can overshoot: `&` alone grows by 4
 * characters once escaped, so a body with enough of them could cross the limit even though its
 * raw, pre-escape length looked safe. This walks the raw string accumulating the *escaped*
 * length each character would add, so the cut point is exact regardless of content.
 */
export function fitCaptionBody(body: string, budget: number): { body: string; truncated: boolean } {
  const trimmed = stripPromptSyntax(String(body ?? ''));
  if (escapeHtml(trimmed).length <= Math.max(0, budget)) return { body: trimmed, truncated: false };

  const ELLIPSIS_COST = 1; // '…' needs no escaping
  let used = ELLIPSIS_COST;
  let cutIndex = 0;
  for (let i = 0; i < trimmed.length; i++) {
    const grown = 1 + (ESCAPE_GROWTH[trimmed[i]!] ?? 0);
    if (used + grown > budget) break;
    used += grown;
    cutIndex = i + 1;
  }
  const cut = trimmed.slice(0, cutIndex);
  const lastSpace = cut.lastIndexOf(' ');
  // Prefer breaking on a space, but not so early that a long final word turns into a stub —
  // under 60% of the safe prefix, the mid-word cut reads better than losing most of the last line.
  const cutAt = lastSpace > cut.length * 0.6 ? lastSpace : cut.length;
  return { body: `${cut.slice(0, cutAt).trim()}…`, truncated: true };
}

/**
 * Build the caption HTML.
 *
 *     <b>Title</b>
 *     ❤️ 2,913 reactions on Civitai      ← only when `engagement` is given
 *     ✍️ HshiKoi                          ← only when a person is found
 *
 *     <pre><code class="language-Prompt">…</code></pre>
 *
 * No hashtag line — Telegram does not index them for in-channel search, and `#image` on a
 * mostly-image channel says nothing a reader did not already know from looking at the photo.
 */
export function renderChannelCaption(input: ChannelCaptionInput): ChannelCaption {
  const head = [`<b>${escapeHtml(input.title)}</b>`];

  if (input.engagement && input.engagement.value > 0 && input.engagement.metric) {
    const { value, metric, sourceName } = input.engagement;
    head.push(
      `❤️ ${value.toLocaleString('en-US')} ${escapeHtml(metric)} on ${escapeHtml(platformLabel(sourceName))}`,
    );
  }

  const person = personForCaption(input.authorAttribution);
  if (person) head.push(`✍️ ${escapeHtml(person)}`);

  const header = head.join('\n');
  // What is left for the body once the header (already in its escaped form — every field pushed
  // onto `head` above was escaped before joining), the blank line separating it from the code
  // block, and the block's own wrapper are paid for. The wrapper's cost is measured from
  // `CODE_BLOCK_OVERHEAD` rather than guessed, so the reserve cannot drift from what it actually
  // costs — the defect D43-first fixed once already, for this exact wrapper.
  const budget = CAPTION_LIMIT - header.length - '\n\n'.length - CODE_BLOCK_OVERHEAD;
  const { body, truncated } = fitCaptionBody(input.body, budget);

  return { text: `${header}\n\n${codeBlock(body)}`, truncated };
}

/**
 * The button row.
 *
 * `📄 Full prompt` appears only when the caption above actually cut the body — below the ceiling
 * the caption already *is* the whole prompt, and the button would promise the reader text they
 * are already looking at. `🚀 Open app` always: `t.me/<bot>?startapp=` is the Main Mini App deep
 * link, not the short-name form (`t.me/<bot>/app?startapp=`), which BotFather has never issued —
 * see DECISIONS D46-second.
 */
export function channelPostButtons(
  botUsername: string,
  promptId: string,
  truncated: boolean,
): { text: string; url: string }[] {
  const buttons: { text: string; url: string }[] = [];
  if (truncated) {
    buttons.push({
      text: '📄 Full prompt',
      url: `https://t.me/${botUsername}?start=prompt_${promptId}`,
    });
  }
  buttons.push({
    text: '🚀 Open app',
    url: `https://t.me/${botUsername}?startapp=prompt_${promptId}`,
  });
  return buttons;
}
